import ImageIcon from 'components/Icon/ImageIcon';
import TextAreaInput from 'components/TextArea';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { device } from 'styles/breakpoints';
import { ActionButton } from 'styles/global.styles';
import { theme } from 'styles/theme';
import { IconCheckmarkSmall, IconCross } from '../../assets/icons';

export interface IMaterial {
  icon?: string;
  type: string;
  selected: boolean;
  details?: string[];
  /** Om aktuellt segment finns i listan: visas under "Förvalda" och startar som vald. */
  preselectedForSegments?: string[];
}

export interface ISavedMaterial {
  type: string;
  description?: string;
}

export interface IMaterialSelector {
  data?: IMaterial[];
  selectedSegment: string;
  otherWasteValue: string;
  onChangeOtherWaste: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onSelectionChange: (index: number, newValue: boolean) => void;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  disabled: boolean;
  error?: boolean | string;
}

const DESCRIPTION_PREVIEW_CHARS = 40;

const ExpandableCardDescription = ({ text }: { text: string }) => {
  const [expanded, setExpanded] = useState(false);
  const needsToggle = text.length > DESCRIPTION_PREVIEW_CHARS;
  const preview = `${text.slice(0, DESCRIPTION_PREVIEW_CHARS)}...`;

  if (!text.trim()) {
    return null;
  }

  if (!needsToggle) {
    return <CardDescription>{text}</CardDescription>;
  }

  return (
    <DescriptionExpandable>
      <CardDescriptionText $expanded={expanded}>{expanded ? text : preview}</CardDescriptionText>
      <ExpandToggle
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setExpanded((prev) => !prev);
        }}
      >
        {expanded ? 'Visa mindre' : 'Visa mer'}
      </ExpandToggle>
    </DescriptionExpandable>
  );
};

const isPreselectedForSegment = (item: IMaterial, segment: string) =>
  !!segment && !!item.preselectedForSegments?.includes(segment);

const MaterialSelector = ({
  data,
  selectedSegment,
  onChangeOtherWaste,
  otherWasteValue,
  onSelectionChange,
  onClick,
  disabled,
  error,
}: IMaterialSelector): JSX.Element => {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState<IMaterial | null>(null);

  const togglePanel = (material: IMaterial | null) => {
    setSelectedMaterial(material);
    setIsPanelOpen(!isPanelOpen);
  };

  useEffect(() => {
    if (isPanelOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isPanelOpen]);

  const preIndices: number[] = [];
  const restIndices: number[] = [];
  data?.forEach((item, index) => {
    if (isPreselectedForSegment(item, selectedSegment)) {
      preIndices.push(index);
    } else {
      restIndices.push(index);
    }
  });

  const renderMaterialCard = (index: number) => {
    const item = data![index];
    const descriptionText = item.details?.join(', ') ?? '';
    return (
      <MaterialItem
        $selected={item.selected}
        key={index}
        role="button"
        tabIndex={0}
        onClick={() => onSelectionChange(index, item.selected)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelectionChange(index, item.selected);
          }
        }}
      >
        <CardTop>
          <IconBadge $selected={item.selected}>
            <BadgeIcon height={28} width={28} src={item.icon} alt="" $onDark={item.selected} />
          </IconBadge>
          <SelectionIndicator $selected={item.selected} aria-hidden>
            {item.selected ? (
              <CheckSvg viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  d="M20 6L9 17l-5-5"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </CheckSvg>
            ) : null}
          </SelectionIndicator>
        </CardTop>
        <CardTitle>{item.type}</CardTitle>
        <ExpandableCardDescription text={descriptionText} />
      </MaterialItem>
    );
  };

  return (
    <>
      <MaterialGrid>
        {selectedSegment !== 'Annat' ? (
          <>
            {preIndices.length > 0 && (
              <MaterialGroup>
                <GroupHeading>Rekommenderade avfallstyper</GroupHeading>
                <GroupGrid>{preIndices.map((i) => renderMaterialCard(i))}</GroupGrid>
              </MaterialGroup>
            )}
            {restIndices.length > 0 && (
              <MaterialGroup>
                {preIndices.length > 0 ? <GroupHeading>Flera avfallstyper</GroupHeading> : null}
                <GroupGrid>{restIndices.map((i) => renderMaterialCard(i))}</GroupGrid>
              </MaterialGroup>
            )}
          </>
        ) : null}

        <TextAreaWrapper>
          <OtherWasteTextArea
            label="Annat avfall?"
            placeholder="Beskrivning"
            onChange={(event) => onChangeOtherWaste(event)}
            value={otherWasteValue}
          />
        </TextAreaWrapper>
      </MaterialGrid>

      <Overlay $visible={isPanelOpen} onClick={() => togglePanel(null)} />
      <Panel $visible={isPanelOpen}>
        {selectedMaterial && (
          <>
            <IconExit onClick={() => togglePanel(null)}>
              <ImageIcon width={24} src={IconCross} alt="Material details exit icon" />
            </IconExit>
            <MaterialType>{selectedMaterial.type}</MaterialType>
            <Details>
              {selectedMaterial.details?.map((detail: string, i: number) => (
                <DetailItem key={i}>
                  <ImageIcon src={IconCheckmarkSmall} alt="Material details checkmark icon" />
                  <span>{detail}</span>
                </DetailItem>
              ))}
            </Details>
          </>
        )}
      </Panel>

      <ActionButton error={error} disabled={disabled} onClick={onClick}>
        Fortsätt
      </ActionButton>
    </>
  );
};

export default MaterialSelector;

const MaterialGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  max-width: 880px;
  width: 100%;
`;

const MaterialGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.columns.gap_medium};
  width: 100%;
`;

const GroupHeading = styled.h4`
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 700;
  color: ${theme.colors.secondary_text};
  text-align: left;
  width: 100%;
  display: flex;
  align-items: center;
  white-space: nowrap;
  gap: ${theme.columns.gap_large};
  margin-bottom: ${theme.columns.gap_large};
  text-transform: uppercase;

  &::before {
    content: '';
    display: block;
    width: 100%;
    height: 1px;
    background-color: ${theme.colors.secondary_text};
  }
  &::after {
    content: '';
    display: block;
    width: 100%;
    height: 1px;
    background-color: ${theme.colors.secondary_text};
  }
`;

const GroupGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: ${theme.columns.gap_large};
  width: 100%;
`;

const MaterialItem = styled.div<{ $selected: boolean }>`
  cursor: pointer;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  text-align: left;

  gap: ${theme.columns.gap_medium};
  background-color: ${theme.colors.content_background};
  border-radius: 24px;
  padding: 1.25rem 1.5rem;
  width: 100%;
  min-height: 0;
  word-break: break-word;

  border: ${({ $selected }) =>
    $selected ? `2px solid ${theme.materials.selectedBorder}` : `1px solid ${theme.colors.border}`};
  box-shadow: ${({ $selected }) =>
    $selected ? '0 8px 24px rgba(25, 113, 73, 0.18)' : '0 2px 8px rgba(0, 0, 0, 0.04)'};

  transition:
    border-color 0.25s ease,
    box-shadow 0.25s ease;

  @media (hover: hover) {
    &:hover {
      border-color: ${({ $selected }) =>
        $selected ? theme.materials.selectedBorder : 'rgba(0, 0, 0, 0.2)'};
    }
  }

  &:focus-visible {
    outline: 2px solid ${theme.materials.selectedBorder};
    outline-offset: 2px;
  }
`;

const CardTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
`;

const IconBadge = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 14px;
  flex-shrink: 0;
  background-color: ${({ $selected }) => ($selected ? theme.materials.selectedBorder : '#f3f4f6')};
  transition: background-color 0.25s ease;
`;

const BadgeIcon = styled(ImageIcon)<{ $onDark: boolean }>`
  object-fit: contain;
  filter: ${({ $onDark }) => ($onDark ? 'brightness(0) invert(1)' : 'none')};
`;

const SelectionIndicator = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  flex-shrink: 0;
  border: ${({ $selected }) => ($selected ? 'none' : '2px solid rgba(0, 0, 0, 0.12)')};
  background-color: ${({ $selected }) =>
    $selected ? theme.materials.selectedBorder : 'transparent'};
  color: ${theme.colors.white};
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease;
`;

const CheckSvg = styled.svg`
  width: 14px;
  height: 14px;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-weight: 700;
  font-size: 1rem;
  line-height: 1.25;
  color: ${theme.colors.primary};
`;

const CardDescription = styled.p`
  margin: 0;
  font-weight: 400;
  font-size: 0.8125rem;
  line-height: 1.45;
  color: ${theme.colors.secondary_text};
  word-break: break-word;
`;

const DescriptionExpandable = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.35rem;
  width: 100%;
`;

const CardDescriptionText = styled.p<{ $expanded: boolean }>`
  margin: 0;
  font-weight: 400;
  font-size: 0.8125rem;
  line-height: 1.45;
  color: ${theme.colors.secondary_text};
  word-break: break-word;
  ${({ $expanded }) =>
    $expanded
      ? `
    max-height: none;
  `
      : `
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
  `}
`;

const ExpandToggle = styled.button`
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${theme.materials.selectedBorder};
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: ${theme.colors.primary};
  }

  &:focus-visible {
    outline: 2px solid ${theme.materials.selectedBorder};
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const SvgButtonWrapper = styled.div`
  -webkit-user-select: none;
  user-select: none;
  cursor: pointer;
`;

const TextAreaWrapper = styled.div`
  display: flex;
  flex-direction: column;

  background-color: ${theme.colors.content_background};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.dimensions.components.borderRadius};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  padding: 1rem;
  height: 100%;
  grid-column: 1 / -1;
`;

const OtherWasteTextArea = styled(TextAreaInput)`
  resize: none;
  height: 100px;
`;

const Overlay = styled.div<{ $visible: boolean }>`
  display: ${({ $visible }) => ($visible ? 'block' : 'none')};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${theme.colors.grayscale_2};
`;

const Panel = styled.div<{ $visible: boolean }>`
  background-color: ${theme.colors.white};
  height: 100vh;
  min-width: 100%;
  top: 0;
  right: 0;
  position: fixed;
  transform: ${({ $visible }) => ($visible ? 'translateX(0)' : 'translateX(100%)')};
  opacity: ${({ $visible }) => ($visible ? '1' : '0')};
  z-index: ${({ $visible }) => ($visible ? '999' : '0')};
  transition:
    transform 0.2s ease-out,
    opacity 0.2s ease-out;

  @media ${device.md} {
    min-width: 50%;
  }

  @media ${device.lg} {
    min-width: 25%;
  }
`;

const Details = styled.div<{ $open?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${theme.columns.gap};
  padding: 1rem;
`;

const MaterialType = styled.h1`
  text-align: center;
  font-weight: 700;
  font-size: 20px;
`;

const DetailItem = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const IconExit = styled(SvgButtonWrapper)`
  position: absolute;
  top: 10px;
  right: 10px;
`;
